import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Heart, TrendingUp, TrendingDown } from 'lucide-react';

const PostReactionButton = ({
  type, // 'like', 'bull', 'bear'
  count = 0,
  isActive = false,
  isLoading = false,
  onClick,
  disabled = false,
}) => {
  const { t } = useTranslation();
  // Pop-in animation the moment this reaction goes from off to on --
  // detected internally rather than threaded through every caller, since
  // `isActive` already tells the whole story.
  const wasActiveRef = useRef(isActive);
  const [justActivated, setJustActivated] = useState(false);
  useEffect(() => {
    if (isActive && !wasActiveRef.current) setJustActivated(true);
    wasActiveRef.current = isActive;
  }, [isActive]);
  const getIcon = () => {
    switch (type) {
      case 'like':
        return <Heart size={16} className={isActive ? "fill-current" : ""} />;
      case 'bull':
        return <TrendingUp size={16} />;
      case 'bear':
        return <TrendingDown size={16} />;
      default:
        return null;
    }
  };

  const getColors = () => {
    const base = "flex items-center space-x-1 px-3 py-1.5 rounded-lg transition-all duration-200 text-sm font-medium";
    
    if (disabled || isLoading) {
      return `${base} text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-50`;
    }

    switch (type) {
      case 'like':
        return isActive
          ? `${base} text-red-700 bg-red-50 border border-red-300 hover:bg-red-100 dark:text-red-400 dark:bg-red-900/30 dark:border-red-500/30 dark:hover:bg-red-900/40`
          : `${base} text-gray-600 dark:text-gray-400 bg-cream-100 dark:bg-gray-800/50 border border-cream-300 dark:border-gray-600/30 hover:bg-cream-900/10 dark:hover:bg-gray-700/50 hover:text-red-700 dark:hover:text-red-400`;
      case 'bull':
        return isActive
          ? `${base} text-green-700 bg-green-50 border border-green-300 hover:bg-green-100 dark:text-green-400 dark:bg-green-900/30 dark:border-green-500/30 dark:hover:bg-green-900/40`
          : `${base} text-gray-600 dark:text-gray-400 bg-cream-100 dark:bg-gray-800/50 border border-cream-300 dark:border-gray-600/30 hover:bg-cream-900/10 dark:hover:bg-gray-700/50 hover:text-green-700 dark:hover:text-green-400`;
      case 'bear':
        return isActive
          ? `${base} text-red-700 bg-red-50 border border-red-300 hover:bg-red-100 dark:text-red-400 dark:bg-red-900/30 dark:border-red-500/30 dark:hover:bg-red-900/40`
          : `${base} text-gray-600 dark:text-gray-400 bg-cream-100 dark:bg-gray-800/50 border border-cream-300 dark:border-gray-600/30 hover:bg-cream-900/10 dark:hover:bg-gray-700/50 hover:text-red-700 dark:hover:text-red-400`;
      default:
        return `${base} text-gray-600 dark:text-gray-400 bg-cream-100 dark:bg-gray-800/50 border border-cream-300 dark:border-gray-600/30`;
    }
  };

  const formatCount = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled || isLoading}
      onAnimationEnd={() => setJustActivated(false)}
      className={`${getColors()} ${justActivated ? 'animate-pop' : ''}`}
      title={t('groups.postButton.reactionTitle', {
        typeLabel: type === 'like' ? t('groups.postButton.typeLike') : type === 'bull' ? t('groups.postButton.typeBull') : type === 'bear' ? t('groups.postButton.typeBear') : type,
      })}
    >
      {isLoading ? (
        <div className="w-4 h-4 border-2 border-gray-500 dark:border-gray-400 border-t-transparent rounded-full animate-spin" />
      ) : (
        getIcon()
      )}
      <span>{formatCount(count)}</span>
    </button>
  );
};

export default PostReactionButton;